import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import mineCraftApi from 'src/api/mineCraftApi';
import minecraftUrls from "src/configs/mine_craft/minecraftUrls";
import { FrameInterface } from 'src/store/mine_craft/framesSlice';
import { usersEntity } from "./usersActions";
import { UserInterface } from "../../store/mine_craft/usersSlice";

const processStrategy = (value: any) => {
  return {
    ...value,
    image_url: minecraftUrls.remoteUrl(value.image_url),
  };
}

export const framesEntity = new schema.Entity(
  'frames',
  {
    user: usersEntity
  },
  {
    processStrategy
  }
);

export const frameEntity = new schema.Entity(
  'frame',
  {
    user: usersEntity
  },
  {
    processStrategy
  }
);

const fetchFramesCollection = async (url: string, params: object = {}) => {
  const response    = await mineCraftApi.get(url, { params })
  const normalized  = normalize<
    any,
    {
      users: { [key: string]: UserInterface };
      frames: { [key: string]: FrameInterface };
    }
  >(response.data.frames, [framesEntity]);

  return {
    users: normalized.entities.users || {},
    frames: normalized.entities?.frames || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      user: { [key: string]: UserInterface };
      frame: FrameInterface;
    }
  >(response.data.frame, frameEntity);
  return normalized.entities;
}

const normalizeUploadInstance = (response: any) => {
  const normalized = normalize<
    any,
    {
      frame: FrameInterface
    }
  >(response.frame, frameEntity);
  return normalized.entities;
}

export const fetchFrames = createAsyncThunk(
  'frames/fetchFrames',
  async (params: object = {}) => {
    return fetchFramesCollection('/onesky/frames', params);
  }
);

export const fetchFrame = createAsyncThunk(
  'frames/fetchFrame',
  async (id: number | string) => {
    const response = await mineCraftApi.get(`/onesky/frames/${id}`);
    return normalizeInstance(response);
  }
);

export const uploadFrame = createAsyncThunk(
  'frames/createFrame',
  async (response: object, { rejectWithValue }) => {
    try {
      return normalizeUploadInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const createFrame = createAsyncThunk(
  'frames/createFrame',
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/frames`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updateFrame = createAsyncThunk(
  'frames/updateFrame',
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.patch(`/onesky/frames/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyFrame = createAsyncThunk(
  'frames/destroyFrame',
  async (id: number) => {
    const response = await mineCraftApi.delete(`/onesky/frames/${id}`);
    return response.data;
  }
);

export const reorderFrames = createAsyncThunk(
  'frames/reorderFrames',
  async ({ ids }: { ids: any }, { rejectWithValue }) => {
    try {
      await mineCraftApi.patch('/onesky/frames/reorder', { ids })

      return { ids };
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);
